import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8889';

const CALL_STATES = {
  IDLE: 'IDLE',
  AGENT_SPEAKING: 'AGENT_SPEAKING',
  LISTENING_FOR_ANSWER: 'LISTENING_FOR_ANSWER',
  PROCESSING_ANSWER: 'PROCESSING_ANSWER',
};

function App() {
  // --- State ---
  const [callState, setCallState]           = useState(CALL_STATES.IDLE);
  const [startTime, setStartTime]           = useState(null);
  const [timer, setTimer]                   = useState('00:00');
  const [durationSeconds, setDurationSeconds] = useState(0);   // countdown target (0 = count up)
  const [interviewStatus, setInterviewStatus] = useState('waiting');
  const [voiceStatus, setVoiceStatus]       = useState('Click microphone to start interview');
  const [progress, setProgress]             = useState(0);
  const [transcripts, setTranscripts]       = useState([]);
  const [notification, setNotification]     = useState(null);
  const [connected, setConnected]           = useState(false);
  const [candidateData, setCandidateData]   = useState(null);
  const [loading, setLoading]               = useState(true);

  // --- Refs ---
  const timerIntervalRef      = useRef(null);
  const timerExpiredCalledRef = useRef(false);   // prevents double-firing timer-expired
  const wsRef                 = useRef(null);
  const sessionIdRef          = useRef(null);
  const interviewIdRef        = useRef(null);
  const audioRef              = useRef(null);
  const mediaRecorderRef      = useRef(null);
  const audioChunksRef        = useRef([]);
  const streamRef             = useRef(null);
  const audioContextRef       = useRef(null);
  const analyserRef           = useRef(null);
  const vadIntervalRef        = useRef(null);
  const silenceStartRef       = useRef(null);
  const isSpeechDetectedRef   = useRef(false);
  const lastPlayStartRef      = useRef(0);
  const lastPlayedUrlRef      = useRef(null);
  const lastPlayedTextRef     = useRef(null);
  const callStateRef          = useRef(callState);

  useEffect(() => { callStateRef.current = callState; }, [callState]);

  // Derived
  const isSpeaking   = callState === CALL_STATES.AGENT_SPEAKING;
  const isListening  = callState === CALL_STATES.LISTENING_FOR_ANSWER;
  const isProcessing = callState === CALL_STATES.PROCESSING_ANSWER;
  const isRecording  = interviewStatus === 'active';

  // --- Read duration URL param once on mount ---
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const dur = parseInt(urlParams.get('duration') || '0');
    if (dur > 0) {
      setDurationSeconds(dur * 60);
      // Show full duration on the timer before interview starts
      const m = String(dur).padStart(2, '0');
      setTimer(`${m}:00`);
    }
  }, []);

  // --- Helpers ---
  const getTokenFromURL = () => {
    const fullUrl  = window.location.href;
    const urlParams = new URLSearchParams(window.location.search);
    const candidateId = urlParams.get('candidateId') || urlParams.get('candidate_id');
    const roleId      = urlParams.get('roleId')      || urlParams.get('role_id');
    if (candidateId && roleId) return fullUrl;
    let token = urlParams.get('token') || urlParams.get('link');
    if (token) {
      try { if (token.includes('%')) token = decodeURIComponent(token); } catch (e) {}
    }
    return token || fullUrl;
  };

  const getSessionId = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('sessionId') || `session-${Date.now()}`;
  };

  const showNotification = useCallback((message, type) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  const updateTranscriptsFromBackend = useCallback((backendTranscript) => {
    if (!backendTranscript || !Array.isArray(backendTranscript)) return;
    const items = [];
    backendTranscript.forEach((item) => {
      if (item.question) items.push({
        id: `q-${item.question_number || items.length + 1}`,
        speaker: 'ai',
        text: item.question,
        timestamp: item.timestamp ? new Date(item.timestamp) : new Date(),
        questionNumber: item.question_number,
      });
      if (item.answer) items.push({
        id: `a-${item.question_number || items.length + 1}`,
        speaker: 'candidate',
        text: item.answer,
        timestamp: item.timestamp ? new Date(item.timestamp) : new Date(),
        questionNumber: item.question_number,
        timeTaken: item.time_taken_seconds,
      });
    });
    setTranscripts(prev => {
      const merged = [...prev];
      items.forEach(newItem => {
        if (!merged.some(m => m.text === newItem.text && m.speaker === newItem.speaker))
          merged.push(newItem);
      });
      return merged.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    });
  }, []);

  const updateProgressFromBackend = useCallback((backendProgress) => {
    if (!backendProgress) return;
    setProgress(Math.round(backendProgress.completion_percentage || 0));
    if (backendProgress.status === 'ended' || backendProgress.status === 'completed') {
      setInterviewStatus('completed');
      setVoiceStatus('Interview completed!');
      setCallState(CALL_STATES.IDLE);
    } else if (backendProgress.status === 'active') {
      setInterviewStatus('active');
    }
  }, []);

  // --- VAD Handlers ---
  const handleSpeechStart = useCallback(() => {
    setCallState(prev => {
      if (prev !== CALL_STATES.AGENT_SPEAKING) return prev;
      if (Date.now() - lastPlayStartRef.current < 1500) return prev; // echo guard
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.onended = null;
        audioRef.current.onerror = null;
        audioRef.current = null;
      }
      setVoiceStatus("Okay, I'm listening...");
      return CALL_STATES.LISTENING_FOR_ANSWER;
    });
  }, []);

  const handleSpeechEnd = useCallback(() => {
    setCallState(prev =>
      prev === CALL_STATES.LISTENING_FOR_ANSWER ? CALL_STATES.PROCESSING_ANSWER : prev
    );
  }, []);

  // --- Audio Player ---
  const playAudio = useCallback(async (audioUrl, isWaitingForNextCommand = false, text = null) => {
    try {
      const isDupUrl  = lastPlayedUrlRef.current === audioUrl;
      const isDupText = text && lastPlayedTextRef.current === text;
      if ((isDupUrl || isDupText) && Date.now() - lastPlayStartRef.current < 2000) return;

      lastPlayStartRef.current  = Date.now();
      lastPlayedUrlRef.current  = audioUrl;
      if (text) lastPlayedTextRef.current = text;

      if (audioRef.current) {
        try { audioRef.current.pause(); audioRef.current.onended = null; audioRef.current.onerror = null; }
        catch (e) {}
        audioRef.current = null;
      }

      setCallState(CALL_STATES.AGENT_SPEAKING);
      setVoiceStatus('Agent is speaking...');

      if (text) {
        setTranscripts(prev => {
          if (prev.some(m => m.text === text && m.speaker === 'ai')) return prev;
          return [...prev, { id: `ai-${Date.now()}`, speaker: 'ai', text, timestamp: new Date().toISOString() }]
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        });
      }

      const audio = new Audio(audioUrl);
      if (audioContextRef.current?.state === 'suspended') await audioContextRef.current.resume();
      audioRef.current = audio;

      audio.onended = () => {
        audioRef.current = null;
        setCallState(prev => prev === CALL_STATES.AGENT_SPEAKING ? CALL_STATES.LISTENING_FOR_ANSWER : prev);
      };
      audio.onerror = () => {
        audioRef.current = null;
        setCallState(CALL_STATES.LISTENING_FOR_ANSWER);
      };
      await audio.play();
    } catch (err) {
      console.error('playAudio error:', err);
      setCallState(CALL_STATES.LISTENING_FOR_ANSWER);
    }
  }, []);

  // --- Timer Expired Handler ---
  const handleTimerExpired = useCallback(async () => {
    try {
      const sessionId = sessionIdRef.current;
      if (!sessionId) return;

      const res = await fetch(
        `${API_BASE_URL}/api/voice/session/${sessionId}/timer-expired`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setInterviewStatus('completed');
          setCallState(CALL_STATES.IDLE);
          if (data.audio_url) {
            await playAudio(`${API_BASE_URL}${data.audio_url}`, false, data.message);
          } else {
            setVoiceStatus('Your interview time has ended. Thank you for your participation.');
          }
        }
      }
    } catch (err) {
      console.error('Timer expired handler error:', err);
      setInterviewStatus('completed');
      setVoiceStatus('Your interview time has ended. Thank you for your participation.');
    }
  }, [playAudio]);

  // --- Voice Backend ---
  const sendVoiceToBackend = useCallback(async (audioBlob) => {
    if (!interviewIdRef.current) {
      setCallState(CALL_STATES.LISTENING_FOR_ANSWER);
      return;
    }
    try {
      setCallState(CALL_STATES.PROCESSING_ANSWER);
      setVoiceStatus('🔄 Processing your response...');

      const formData = new FormData();
      formData.append('audio', new File([audioBlob], 'voice.webm', { type: 'audio/webm;codecs=opus' }));
      formData.append('interview_id', interviewIdRef.current);

      const response = await fetch(`${API_BASE_URL}/api/voice/interview`, { method: 'POST', body: formData });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      if (data.success) {
        if (data.progress) updateProgressFromBackend(data.progress);
        if (data.transcribed_text) {
          setTranscripts(prev => {
            if (prev.some(m => m.text === data.transcribed_text && m.speaker === 'candidate')) return prev;
            return [...prev, { id: `user-${Date.now()}`, speaker: 'candidate', text: data.transcribed_text, timestamp: new Date().toISOString() }]
              .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          });
        }
        if (data.audio_url) {
          await playAudio(`${API_BASE_URL}${data.audio_url}`, data.waiting_for_next || false, data.chatbot_response);
        } else if (data.chatbot_response) {
          setTranscripts(prev => [...prev, { id: `ai-${Date.now()}`, speaker: 'ai', text: data.chatbot_response, timestamp: new Date().toISOString() }]);
        }
        if (data.interview_completed) {
          setInterviewStatus('completed');
          setVoiceStatus('Interview completed!');
          setCallState(CALL_STATES.IDLE);
          showNotification('Interview completed successfully!', 'success');
        } else {
          setCallState(CALL_STATES.LISTENING_FOR_ANSWER);
        }
      } else {
        throw new Error(data.error || 'Failed to process voice');
      }
    } catch (error) {
      console.error('❌ Error sending voice to backend:', error);
      showNotification(error.message || 'Failed to process voice. Please try again.', 'error');
      setCallState(CALL_STATES.LISTENING_FOR_ANSWER);
    }
  }, [playAudio, showNotification, updateProgressFromBackend]);

  // --- VAD ---
  const startVAD = useCallback((stream) => {
    if (vadIntervalRef.current) return;
    try {
      if (!audioContextRef.current)
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const context = audioContextRef.current;
      if (context.state === 'suspended') context.resume();
      const source  = context.createMediaStreamSource(stream);
      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray    = new Uint8Array(bufferLength);

      const checkVAD = () => {
        if (!analyserRef.current) return;
        analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) sum += dataArray[i];
        const average   = sum / bufferLength;
        const threshold = callStateRef.current === CALL_STATES.AGENT_SPEAKING ? 45 : 20;

        if (average > threshold) {
          if (!isSpeechDetectedRef.current) { isSpeechDetectedRef.current = true; handleSpeechStart(); }
          silenceStartRef.current = null;
        } else {
          if (isSpeechDetectedRef.current) {
            if (!silenceStartRef.current) silenceStartRef.current = Date.now();
            else if (Date.now() - silenceStartRef.current > 3500) {
              isSpeechDetectedRef.current = false;
              handleSpeechEnd();
              silenceStartRef.current = null;
            }
          }
        }
        vadIntervalRef.current = requestAnimationFrame(checkVAD);
      };
      checkVAD();
    } catch (err) { console.error('Error starting VAD:', err); }
  }, [handleSpeechStart, handleSpeechEnd]);

  const startContinuousCapture = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 44100, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
      streamRef.current = stream;
      startVAD(stream);
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorder.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm;codecs=opus' });
        audioChunksRef.current = [];
        if (blob.size > 1000) await sendVoiceToBackend(blob);
        else setCallState(CALL_STATES.LISTENING_FOR_ANSWER);
      };
      return true;
    } catch (error) {
      console.error('❌ Error starting capture:', error);
      showNotification('Failed to access microphone.', 'error');
      return false;
    }
  }, [sendVoiceToBackend, startVAD, showNotification]);

  // State transitions for recording
  useEffect(() => {
    if (callState === CALL_STATES.LISTENING_FOR_ANSWER) {
      if (mediaRecorderRef.current?.state === 'inactive') {
        audioChunksRef.current = [];
        mediaRecorderRef.current.start(100);
        setVoiceStatus('🎤 Listening...');
      }
    }
    if (callState === CALL_STATES.PROCESSING_ANSWER) {
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
        setVoiceStatus('🔄 Processing...');
      }
    }
  }, [callState]);

  // --- Start Interview ---
  const startInterview = async () => {
    try {
      const sessionId = sessionIdRef.current || getSessionId();
      const token     = getTokenFromURL();
      setVoiceStatus('Starting interview...');
      const response = await fetch(`${API_BASE_URL}/api/voice/session/${sessionId}/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!response.ok) throw new Error('Failed to create session');
      const data = await response.json();

      if (data.already_completed) {
        setInterviewStatus('completed');
        setVoiceStatus('You have already completed this interview. This link has expired.');
        return;
      }

      if (data.success) {
        interviewIdRef.current = data.interview_id;
        if (data.candidate_data) setCandidateData(data.candidate_data);
        timerExpiredCalledRef.current = false;
        setStartTime(Date.now());
        setInterviewStatus('active');
        setProgress(0);
        setTranscripts([]);
        const captureStarted = await startContinuousCapture();
        if (!captureStarted) throw new Error('Failed to start microphone');
        if (data.audio_url) await playAudio(`${API_BASE_URL}${data.audio_url}`, false, data.question_data?.question);
        else setCallState(CALL_STATES.LISTENING_FOR_ANSWER);
      }
    } catch (error) {
      console.error('Error starting interview:', error);
      showNotification(error.message, 'error');
      setCallState(CALL_STATES.IDLE);
    }
  };

  const toggleVoice = () => {
    if (interviewStatus === 'waiting' || interviewStatus === 'idle') { startInterview(); return; }
    if (callState === CALL_STATES.LISTENING_FOR_ANSWER) setCallState(CALL_STATES.PROCESSING_ANSWER);
  };

  // --- Init: check already_completed via link-info ---
  useEffect(() => {
    const initializeApp = async () => {
      try {
        setLoading(true);
        const token     = getTokenFromURL();
        const sessionId = getSessionId();
        sessionIdRef.current = sessionId;
        if (token) {
          const res = await fetch(`${API_BASE_URL}/api/interview/link-info`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          });
          if (res.ok) {
            const d = await res.json();
            if (d.already_completed) {
              setInterviewStatus('completed');
              setVoiceStatus('You have already completed this interview. This link has expired.');
              return;
            }
            if (d.success && d.candidate_data) setCandidateData(d.candidate_data);
          }
        }
      } catch (err) {
        console.error('Init error:', err);
      } finally {
        setLoading(false);
      }
    };
    initializeApp();
  }, []);

  // --- WebSocket ---
  useEffect(() => {
    const sessionId  = sessionIdRef.current || getSessionId();
    if (!sessionId) return;
    const wsBaseUrl  = API_BASE_URL.replace('http://', 'ws://').replace('https://', 'wss://');
    const ws         = new WebSocket(`${wsBaseUrl}/api/voice/ws/${sessionId}`);
    wsRef.current    = ws;
    ws.onopen  = () => setConnected(true);
    ws.onerror = () => setConnected(false);
    ws.onclose = () => setConnected(false);
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        switch (msg.type) {
          case 'state_update':
            if (msg.data.transcript)   updateTranscriptsFromBackend(msg.data.transcript);
            if (msg.data.progress)     updateProgressFromBackend(msg.data.progress);
            if (msg.data.interview_id) { interviewIdRef.current = msg.data.interview_id; }
            if (msg.data.candidate_data) setCandidateData(msg.data.candidate_data);
            break;
          case 'audio_update':
            if (msg.data.audio_url) playAudio(`${API_BASE_URL}${msg.data.audio_url}`, msg.data.waiting_for_next || false, msg.data.text);
            break;
          case 'transcript_update': updateTranscriptsFromBackend(msg.data.transcript || []); break;
          case 'progress_update':   updateProgressFromBackend(msg.data); break;
          case 'interview_completed': setInterviewStatus('completed'); setCallState(CALL_STATES.IDLE); break;
          default:
            console.debug('Unhandled WS message type:', msg.type);
            break;
        }
      } catch (err) { console.error('WS parse error:', err); }
    };
    const ping = setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' })); }, 30000);
    return () => { clearInterval(ping); if (ws.readyState === WebSocket.OPEN) ws.close(); };
  }, [updateTranscriptsFromBackend, updateProgressFromBackend, playAudio]);

  // --- Countdown Timer ---
  useEffect(() => {
    if (isRecording && startTime) {
      timerIntervalRef.current = setInterval(() => {
        if (durationSeconds > 0) {
          // Countdown mode
          const elapsed    = Math.floor((Date.now() - startTime) / 1000);
          const remaining  = Math.max(0, durationSeconds - elapsed);
          const m = Math.floor(remaining / 60);
          const s = remaining % 60;
          setTimer(`${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);

          if (remaining === 0 && !timerExpiredCalledRef.current) {
            timerExpiredCalledRef.current = true;
            handleTimerExpired();
          }
        } else {
          // Count-up mode (no duration configured)
          const elapsed = Date.now() - startTime;
          const m = Math.floor(elapsed / 60000);
          const s = Math.floor((elapsed % 60000) / 1000);
          setTimer(`${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
        }
      }, 1000);
    } else {
      if (timerIntervalRef.current) { clearInterval(timerIntervalRef.current); timerIntervalRef.current = null; }
      // Reset display: show full duration if configured, else 00:00
      if (durationSeconds > 0) {
        const m = Math.floor(durationSeconds / 60);
        const s = durationSeconds % 60;
        setTimer(`${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
      } else {
        setTimer('00:00');
      }
    }
    return () => { if (timerIntervalRef.current) clearInterval(timerIntervalRef.current); };
  }, [isRecording, startTime, durationSeconds, handleTimerExpired]);

  // --- Cleanup on unmount ---
  useEffect(() => {
    return () => {
      if (vadIntervalRef.current)   cancelAnimationFrame(vadIntervalRef.current);
      if (audioRef.current)         audioRef.current.pause();
      if (mediaRecorderRef.current?.state !== 'inactive') mediaRecorderRef.current?.stop();
      if (streamRef.current)        streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  if (loading) return <div className="loading-screen">Loading interview...</div>;

  const getStatusBadgeClass   = () => `status-badge status-${interviewStatus}`;
  const getStatusBadgeContent = () =>
    interviewStatus === 'active'    ? 'Active'    :
    interviewStatus === 'completed' ? 'Completed' : 'Waiting';

  return (
    <div className="App">
      <div className="animated-bg"></div>
      <div className="main-container">
        <div className="interview-card">

          <div className="header-section">
            <div className="logo-container"><i className="fas fa-robot"></i></div>
            <h1>AI Interview Assistant</h1>
            <p className="subtitle">Premium Voice Interview Experience</p>
            <div className={`connection-status ${connected ? 'connected' : 'disconnected'}`}>
              {connected ? '🟢 Connected' : '🔴 Connection Offline'}
            </div>
          </div>

          <div className="candidate-info">
            <div className="info-row">
              <span className="info-label"><i className="fas fa-user"></i> Name</span>
              <span className="info-value">{candidateData?.name || candidateData?.full_name || 'N/A'}</span>
            </div>
            <div className="info-row">
              <span className="info-label"><i className="fas fa-briefcase"></i> Position</span>
              <span className="info-value">{candidateData?.position || 'N/A'}</span>
            </div>
            <div className="info-row">
              <span className="info-label"><i className="fas fa-info-circle"></i> Status</span>
              <span className={getStatusBadgeClass()}>{getStatusBadgeContent()}</span>
            </div>
          </div>

          <div className="voice-controls">
            <div className="voice-visualizer">
              <button
                className={`voice-btn ${isRecording ? 'active' : ''} ${isSpeaking ? 'speaking' : ''} ${isListening ? 'listening' : ''} ${isProcessing ? 'processing' : ''}`}
                onClick={toggleVoice}
                aria-label="Toggle Interview Mic"
              >
                {isSpeaking    ? <i className="fas fa-robot"></i>             :
                 isProcessing  ? <i className="fas fa-spinner fa-spin"></i>   :
                 isRecording   ? <i className="fas fa-microphone-alt"></i>    :
                                 <i className="fas fa-microphone"></i>}
              </button>
              {(isSpeaking || isListening) && (
                <div className={`wave-bars ${isSpeaking ? 'speaking-waves' : 'listening-waves'}`}>
                  <div className="wave-bar"></div><div className="wave-bar"></div>
                  <div className="wave-bar"></div><div className="wave-bar"></div>
                  <div className="wave-bar"></div>
                </div>
              )}
            </div>
            <div className="voice-status-text">
              {isSpeaking   ? <span className="speaking-indicator">Agent Speaking...</span>  :
               isListening  ? <span className="listening-indicator">Listening...</span>       :
               isProcessing ? <span className="processing-indicator">Processing...</span>     :
                              voiceStatus}
            </div>
            <div className="timer">{timer}</div>
          </div>

          <div className="progress-section">
            <div className="progress-label"><span>Progress</span><span>{progress}%</span></div>
            <div className="progress-bar-custom">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
          </div>

          <div className="transcript-section">
            <h3><i className="fas fa-comments"></i> Live Transcript</h3>
            <div className="transcript-list">
              {transcripts.map((t, idx) => (
                <div key={idx} className={`transcript-item transcript-${t.speaker}`}>
                  <div className="transcript-speaker">
                    <i className={`fas ${t.speaker === 'ai' ? 'fa-robot' : 'fa-user'}`}></i>
                    {t.speaker === 'ai' ? ' AI' : ' Candidate'}
                  </div>
                  <div className="transcript-text">{t.text}</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
      {notification && (
        <div className={`notification ${notification.type}`}>{notification.message}</div>
      )}
    </div>
  );
}

export default App;